import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatLegacyDialog as MatDialog, MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { LocalStorageManager } from '@cutting/main/shared/helper/local-storage-manager.helper';
import { UserSettingsService } from '@cutting/main/shared/services/user-settings.service';
import { UserService } from '@cutting/main/shared/services/user.service';
import { fuseAnimations } from '@fuse/animations';
import { CuttingClient, CuttingClientType, ExtendedAddress, MyValidator, User } from 'cloud-shared-lib';
import { Observable, of } from 'rxjs';
import { catchError, concatMap, debounceTime, filter, map, switchMap, tap } from 'rxjs/operators';
import { ProfileService } from '../profile.service';
import { CuttingAppointManagerDialogComponent } from './cutting-appoint-manager-dialog/cutting-appoint-manager-dialog.component';

interface SaveErrorResponse {
  code: number;
  message: string;
}

@Component({
  selector: 'fuse-profile-about',
  templateUrl: './about.component.html',
  styleUrls: ['./about.component.scss'],
  animations: fuseAnimations,
})
export class FuseProfileAboutComponent implements OnInit {
  about: CuttingClient = undefined;
  physicalClientEditForm: UntypedFormGroup;
  clientType = CuttingClientType;
  submiting = false;
  appointManagerDialogRef: MatDialogRef<CuttingAppointManagerDialogComponent>;
  usePhoneNumber = true;
  requiredINN = true;
  allowClientsChooseManager = true;

  isRussian = true;
  userLang = 'en';

  addressResponse$: Observable<ExtendedAddress[]>;

  constructor(
    private profileService: ProfileService,
    private userService: UserService,
    private fb: UntypedFormBuilder,
    private snackBar: MatSnackBar,
    private dialog: MatDialog,
    private userSettingsService: UserSettingsService
  ) {
    this.isRussian = +LocalStorageManager.client.countryCode === 643;
    this.userLang = LocalStorageManager.client.languageCode;

    this.profileService.aboutOnChanged
      .pipe(
        tap(about => {
          this.about = about as CuttingClient;

          this.createForms();
          this.setFormsData();
        }),
        concatMap(() => this.userSettingsService.getCuttingSettings()),
        tap(response => {
          this.usePhoneNumber = response.clientReginstering.requiredPhoneNumber;
          this.requiredINN =
            response.clientReginstering.requiredINN && this.about.type === CuttingClientType.JuridicalPerson;
          this.allowClientsChooseManager = response.allowClientsChooseManager;

          if (this.requiredINN) {
            this.physicalClientEditForm.controls.inn.setValidators(Validators.required);
          } else {
            this.physicalClientEditForm.controls.inn.clearValidators();
          }

          if (this.usePhoneNumber) {
            this.physicalClientEditForm.controls.phone.setValidators([Validators.required, Validators.maxLength(50)]);
          } else {
            this.physicalClientEditForm.controls.phone.setValidators(Validators.maxLength(50));
          }

          this.physicalClientEditForm.updateValueAndValidity();
        })
      )
      .subscribe();
  }

  ngOnInit(): void {
    this.addressResponse$ = this.physicalClientEditForm.controls.defaultDeliveryAddress.valueChanges.pipe(
      filter(val => typeof val === 'string'),
      debounceTime(200),
      switchMap(val => this.getSuggestions(val)),
      catchError(() => {
        this.snackBar.open($localize`Ошибка загрузки подсказок`);

        return of([]);
      })
    );
  }

  setFormsData(): void {
    this.physicalClientEditForm.patchValue(this.about);
  }

  createForms(): void {
    this.physicalClientEditForm = this.fb.group({
      companyName: ['', Validators.maxLength(50)],
      surname: ['', Validators.compose([Validators.maxLength(30), Validators.required])],
      name: ['', Validators.compose([Validators.maxLength(30), Validators.required])],
      patronymic: ['', Validators.maxLength(30)],
      phone: ['', [Validators.maxLength(50)]],
      defaultDeliveryAddress: ['', [MyValidator.autocompleteObjectValidator()]],
      inn: [''],
    });
  }

  addressAutocompleteClosed(): void {
    if (typeof this.physicalClientEditForm.controls.defaultDeliveryAddress.value === 'string') {
      this.physicalClientEditForm.controls.defaultDeliveryAddress.setValue('');
    }
  }

  addressAutocompleteDisplayFn(val: ExtendedAddress): any {
    return val?.unrestricted_value;
  }

  saveFormData(callback?: () => void): void {
    this.about.surname = this.physicalClientEditForm.controls.surname.value;
    this.about.name = this.physicalClientEditForm.controls.name.value;
    this.about.patronymic = this.physicalClientEditForm.controls.patronymic.value;
    this.about.phone = this.physicalClientEditForm.controls.phone.value;
    this.about.companyName = this.physicalClientEditForm.controls.companyName.value;
    this.about.defaultDeliveryAddress = this.physicalClientEditForm.controls.defaultDeliveryAddress.value;
    this.about.INN = this.physicalClientEditForm.controls.inn.value;

    this.userService.saveClient(this.about).subscribe(
      (cl: CuttingClient) => {
        this.refreshDataInLocalStorage(cl);

        if (callback !== undefined && callback !== null) {
          callback();
        }
      },
      (error: HttpErrorResponse) => {
        this.submiting = false;

        if (+error.status === 400) {
          const badReqStat: SaveErrorResponse = error.error;

          switch (badReqStat.code) {
            case 0:
              this.snackBar.open($localize`Email и телефон не указаны`);
              break;
            case 1:
              this.snackBar.open($localize`Email уже зарегистрирован`);
              break;
            case 2:
              this.snackBar.open($localize`Номер телефона уже зарегистрирован`);
              break;
          }
        }
      }
    );
  }

  getShortName(client: CuttingClient): string {
    if (client.type === CuttingClientType.JuridicalPerson) {
      return client.companyName;
    } else {
      let shortName = '';
      if (client.surname !== '') {
        shortName += client.surname;
      }
      if (client.name !== '') {
        shortName += client.name[0] + '.';
      }
      if (client.patronymic !== '') {
        shortName += ' ' + client.patronymic[0] + '.';
      }

      return shortName;
    }
  }

  onSubmitPhysicalClientEditForm(): void {
    if (!this.physicalClientEditForm.valid) {
      return;
    }

    this.submiting = true;
    if (this.physicalClientEditForm.valid) {
      this.saveFormData(() => {
        this.submiting = false;

        this.snackBar.open($localize`Изменения сохранены`);
      });
    }
  }

  openAppointManagerDialog(): void {
    this.appointManagerDialogRef = this.dialog.open(CuttingAppointManagerDialogComponent, {
      panelClass: 'cutting-appoint-manager-detail-dialog',
      width: '500px',
      disableClose: false,
    });

    this.appointManagerDialogRef.afterClosed().subscribe((result: User | undefined) => {
      if (result) {
        this.about.personalManagerId = result.id;
        this.about.personalManagerFullName = `${result.personSurname} ${result.personName} ${result.personPatronymic}`;
      }

      this.appointManagerDialogRef = null;
    });
  }

  removePersonalManager(): void {
    this.about.personalManagerId = undefined;
    this.about.personalManagerFullName = '';
  }

  private refreshDataInLocalStorage(newClient: CuttingClient): void {
    const LSC = LocalStorageManager.client;

    if (!LSC) {
      return;
    }

    LSC.personShortName = this.getShortName(this.about);
    LSC.companyName = this.about.companyName;

    LocalStorageManager.client = LSC;
  }

  private getSuggestions(query: string): Observable<ExtendedAddress[]> {
    if (this.isRussian) {
      return this.profileService.gatDadataAddressSuggestions(query) as Observable<ExtendedAddress[]>;
    } else {
      return this.getPlaceAutocomplete(query);
    }
  }

  private getPlaceAutocomplete(query: string): Observable<ExtendedAddress[]> {
    return this.profileService.gatGoogleAddressSuggestions(query, this.userLang).pipe(
      map(res =>
        res.predictions.map(
          p =>
            ({
              data: null,
              googleData: p,
              value: p.description,
              // eslint-disable-next-line @typescript-eslint/naming-convention
              unrestricted_value: p.description,
            } as ExtendedAddress)
        )
      )
    );
  }
}
